import React from "react";
import Moment from 'moment';
import { MenuItem, Select, TextField } from "@material-ui/core";
import UsersAutocomplete, { OptionType } from "./UsersAutocomplete";
import QdDatePicker from "./Datetime/QdDatePicker";
import NumberFormat from "react-number-format";
import { useTranslation } from "react-i18next";

import "./WorkCostForm.css";
import UserTooltip from "./UserTooltip";

export interface IWorkCostData {
	description: string,
	time: string,
	additionalCost: string,
	date: Moment.Moment,
	workType: string,
	user: OptionType,
}

function WorkTypeEnum(props: { value: string, onChange: (e: React.ChangeEvent<{ name?: string, value: string }>) => void }) {
	const { t } = useTranslation();

	return <Select
		value={props.value}
		onChange={props.onChange}
		variant="outlined"
	>
		<MenuItem value="DuringWorkingHours">{t("control.workCost.workEnum_DuringWorkingHours")}</MenuItem>
		<MenuItem value="AfterWorkingHours">{t("control.workCost.workEnum_AfterWorkingHours")}</MenuItem>
		<MenuItem value="DayOff">{t("control.workCost.workEnum_DayOff")}</MenuItem>
	</Select>
}

interface NumberFormatCustomProps {
	inputRef: (instance: NumberFormat | null) => void;
	onChange: (event: { target: { value: string } }) => void;
}

function NumberFormatCustom(props: NumberFormatCustomProps) {
	const { inputRef, onChange, ...other } = props;

	return (
		<NumberFormat
			{...other}
			getInputRef={inputRef}
			onValueChange={values => {
				onChange({
					target: {
						value: values.value,
					},
				});
			}}
			thousandSeparator=" "
			decimalSeparator=","
			isNumericString
		/>
	);
}

interface IWorkCostFormProps {
	isAdmin: boolean,
	value: IWorkCostData,
	onChange: (value: IWorkCostData) => void,
}

export default function WorkCostForm(props: IWorkCostFormProps) {

	const { t } = useTranslation();

	function handleChange(prop: keyof IWorkCostData, value: any) {
		let v = { ...props.value };
		v[prop] = value;
		props.onChange(v);
	}

	if (!props.value)
		return null;

	return (<div className="layout-grid workCostDataEditor">
		{props.isAdmin && <div className="row form-group">
			<div className="col-12">
				<div className="form-field input-group">
					<span className="input-group-addon">{t("control.workCost.user")}</span>
					<UsersAutocomplete value={props.value.user} onChange={(e) => handleChange("user", e)} />
				</div>
			</div>
		</div>}
		{!props.isAdmin && props.value.user && <div className="row form-group">
			<div className="col-12">
				<div className="form-field input-group">
					<span className="input-group-addon">{t("control.workCost.user")}</span>
					<UserTooltip id={props.value.user.value} name={props.value.user.label} />
				</div>
			</div>
		</div>}
		<div className="row form-group">
			<div className="col-12">
				<div className="form-field input-group">
					<span className="input-group-addon">{t("control.workCost.workType")}</span>
					<WorkTypeEnum value={props.value.workType} onChange={(e) => handleChange("workType", e.target.value)} />
				</div>
			</div>
		</div>
		<div className="row form-group">
			<div className="col-12">
				<div className="form-field input-group">
					<span className="input-group-addon">{t("control.workCost.workTime")}</span>
					<QdDatePicker value={props.value.date} onChange={(result) => handleChange("date", result)} />
					<TextField
						variant="outlined"
						value={props.value.time}
						onChange={(e) => handleChange("time", e.target.value)}
						placeholder="1d5h25m"
					/>
				</div>
			</div>
		</div>
		<div className="row form-group">
			<div className="col-12">
				<div className="form-field input-group">
					<span className="input-group-addon">{t("control.workCost.additionalCost")}</span>
					<TextField
						variant="outlined"
						value={props.value.additionalCost}
						onChange={(e) => handleChange("additionalCost", e.target.value)}
						InputProps={{
							inputComponent: NumberFormatCustom as any,
						}}
					/>
					<span>{t("control.workCost.PLN")}</span>
				</div>
			</div>
		</div>
		<div className="row form-group form-field">
			<textarea
				style={{width: '100%'}}
				value={props.value.description}
				onChange={(e) => handleChange("description", e.target.value)}
				placeholder={t("control.workCost.descriptionPlaceholder")}
			/>
		</div>
	</div>);
}